<template>
  <div class="card mb-4">
    <div class="card-header">
      Custom Categories
    </div>
    <div class="card-body" v-if="!testCategories.length">
      <p class="text-center p-4">Loading Categories...</p>
    </div>
    <div class="card-body" v-if="isInvestigation">
      <p class="card-text p-2">
        Enable between 4 to 7 categories most relevant to this investigation from the list below (Honesty &amp; Integrity is included by default). Emphasize the
        categories that are the focus of the investigation, if needed. You may choose up to two.
      </p>
    </div>
    <div class="card-body">
      <div v-if="testCategories.length">
        <div class="alert alert-info text-small m-2" :class="showErrorAlert ? 'alert-danger' : 'alert-info'">
          <h6 class="alert-heading">
            <fa v-if="!showErrorAlert" icon="info-circle" />
            <fa v-if="showErrorAlert" icon="exclamation-circle" />
            Category and Emphasis selection
          </h6>
          <ul class="list-unstyled mb-0">
            <li><small>A minmum of five categories must be enabled, a maximum of eight.</small></li>
            <li><small>A maximum of two categories may be emphasized.</small></li>
          </ul>
        </div>
      </div>
      <div v-if="testCategories.length" class="table-responsive">
        <table class="table">
          <thead>
            <tr>
              <th scope="col" class="border-top-0">Category</th>
              <th scope="col" class="text-center border-top-0 header-enabled">
                Enabled
                <span data-toggle="tooltip" data-placement="top" title="Enables or disables the category during the interview.">
                  <fa icon="question-circle" fixed-width />
                </span>
                <br />
                ({{ enabledCount }})
              </th>
              <th scope="col" class="text-center border-top-0 header-emphasized">
                Emphasized
                <span data-toggle="tooltip" data-placement="top" title="Emphasizes the category during the interview.">
                  <fa icon="question-circle" fixed-width />
                </span>
                <br />
                ({{ emphasizedCount }})
              </th>
            </tr>
          </thead>
          <tbody>
            <tr colspan="12" v-if="!testCategories.length" class="text-muted text-center">
              <td colspan="12">
                No categories
              </td>
            </tr>
            <tr v-for="category in testCategories" :key="category.id">
              <td>
                <span class="pt-2" data-toggle="tooltip" data-placement="top" :title="category.description || 'N/A'">
                  {{ category.displayName }} <fa icon="question-circle" fixed-width />
                </span>
              </td>
              <td class="text-center">
                <div class="custom-control custom-switch">
                  <input
                    type="checkbox"
                    class="custom-control-input"
                    :id="`${category.id}-enable`"
                    :checked="category.status === 'enabled'"
                    :disabled="loadingMessage || category.isRequired"
                    @change="toggleEnabled(category, $event)"
                  />
                  <label class="custom-control-label" :for="`${category.id}-enable`"></label>
                </div>
              </td>
              <td class="text-center">
                <div v-if="!category.hasEmphasis">
                  <abbr class="text-muted initialism" title="Emphasis is not available for this category.">n/a</abbr>
                </div>
                <div v-if="category.hasEmphasis" class="custom-control custom-switch">
                  <input
                    type="checkbox"
                    class="custom-control-input"
                    :id="`${category.id}-emphasize`"
                    :checked="category.isEmphasized"
                    :disabled="loadingMessage || category.isRequired || category.status !== 'enabled'"
                    @change="toggleIsEmphasized(category, $event)"
                  />
                  <label class="custom-control-label" :for="`${category.id}-emphasize`"></label>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import $ from "jquery";

export default {
  props: {
    organizationId: String,
  },
  data() {
    return {
      loadingMessage: null,
      formWasValidated: false,
      error: null,

      test: null,
      testCategories: [],
    };
  },
  created() {
    this.getTest();
  },
  updated() {
    $('[data-toggle="tooltip"]').tooltip({ boundary: "window" });
  },
  computed: {
    ...mapGetters("user", ["user"]),
    showErrorAlert() {
      return this.testCategories.length && (this.showMaxCategoriesError || this.showMaxEmphasisError);
    },
    enabledCount() {
      return this.testCategories.filter(c => c.status === "enabled").length;
    },
    emphasizedCount() {
      return this.testCategories.filter(c => c.status === "enabled" && c.isEmphasized == true).length;
    },
    showMaxCategoriesError() {
      return this.enabledCount > 8;
    },
    showMaxEmphasisError() {
      return this.emphasizedCount > 2;
    },
    testId() {
      return this.test ? this.test.testId : null;
    },
    testCode() {
      return this.test ? this.test.testCode : null;
    },
    isInvestigation() {
      return this.testCode == "investigations-v2021.12.06";
    },
  },
  watch: {
    organizationId() {
      if (this.organizationId) {
        this.getTest();
      }
    },
    testId() {
      if (this.testId) {
        this.getTestCategories();
      }
    },
    testCategories() {
      if (this.testCategories) {
        this.$emit("test-categories-selected-changed", this.getSelectedCategories());
      }
    },
  },
  methods: {
    getTest() {
      this.$axios
        .get(`/v1/organizations/${this.organizationId}/investigations/test-id`)
        .then(res => (this.test = res.data))
        .catch(() => this.$router.push("/404"));
    },
    getTestCategories() {
      this.$axios
        .get(`/v1/organizations/${this.organizationId}/users/${this.user.id}/tests/${this.testId}/categories`)
        .then(res => res.data)
        .then(items => items.map(i => ({ ...i, status: i.isRequired ? "enabled" : "disabled", isEmphasized: false })))
        .then(items => (this.testCategories = items))
        .catch(() => this.$router.push("/404"));
    },
    getSelectedTestId() {
      return this.testId;
    },
    getSelectedCategories() {
      return this.testCategories.filter(c => c.status === "enabled");
    },
    reset() {
      this.loadingMessage = false;
      this.formWasValidated = false;

      this.selectedTestId = null;
      this.testCategories = [];
    },
    toggleIsEmphasized(item, $event) {
      if ($event.target.checked) {
        item.isEmphasized = true;
      } else {
        item.isEmphasized = false;
      }

      // force binding by updating the reference being enumerated
      this.testCategories = this.testCategories.slice();
    },
    toggleEnabled(item, $event) {
      if ($event.target.checked) {
        item.status = "enabled";
        item.isEmphasized = false;
      } else {
        item.status = "disabled";
        item.isEmphasized = false;
      }

      // force binding by updating the reference being enumerated
      this.testCategories = this.testCategories.slice();
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@/assets/styles/base.scss";

.card-body {
  @extend .p-0;
}

.table-responsive {
  height: 400px;
  overflow-y: scroll;

  table {
    margin-bottom: 0;

    thead tr:nth-child(1) th {
      background: white;
      position: sticky;
      top: 0;
      z-index: 10;
    }

    .header-enabled {
      width: 100px;
      @extend .pl-0;
    }

    .header-emphasized {
      width: 130px;
      @extend .pl-0;
    }
  }
}

.custom-control-label {
  cursor: pointer;
}
</style>
